'use strict';

const libPictView = require('pict-view');
const libModal = require('pict-section-modal');
const libChecklist = require('pict-section-checklist');
const libPlansheetChecklistProvider = require('../providers/PlansheetChecklistProvider.js');
const libLoopStatus = require('../LoopStatus.js');
const libFocus = require('../FocusFilters.js');

/**
 * VisionLoops -- the four refinement loops around a vision, drawn as a cycle: Design -> Build ->
 * Deliver -> Gather Feedback -> back to Design. Each loop is now a CHECKLIST: the ring is that
 * checklist's weighted completion (computed server-side, LoopsAPI). Click a ring to open its
 * checklist below; checking items persists and the ring updates live (the checklist's onChange
 * re-fetches the loops and repaints only the ring cycle, so the open checklist stays mounted).
 *
 * Embedded on the Vision detail; configure(IDVision) loads + paints it into #Entity-VisionLoops.
 * The loop checklists are shared, vision-owned, and bound by Context ({ OwnerType: 'Checklist',
 * IDOwner: <IDChecklist> }) through the same PlansheetChecklistProvider the work-item embed uses.
 */
const _CIRC = 213.6; // 2 * pi * r, r = 34
const _COLORS = { design: '#2880a6', build: '#d9923c', deliver: '#7d63b8', feedback: '#16a085' };
// A stopped loop is not a finished one and must not wear the loop's own colour. Grey reads as closed
// without reading as progress, which is exactly the distinction an abandoned vision needs.
const _STOPPED = '#98a0b0';

class PictViewVisionLoops extends libPictView
{
	onBeforeInitialize()
	{
		this._id = 0;
		this._loops = [];
		this._openLoop = null;
		this._provider = new libPlansheetChecklistProvider(this.pict);
		this._childUp = {};
		return super.onBeforeInitialize();
	}

	_api() { return this.pict.providers.LoopsAPI; }

	configure(pIDVision)
	{
		this._id = parseInt(pIDVision, 10) || 0;
		// Which loop is open comes off the URL, so a link to "this vision's Deliver checklist" opens it.
		this._openLoop = libFocus.readHashParams().loop || null;
		let tmpAPI = this._api();
		if (!tmpAPI || !this._id) { return Promise.resolve(); }
		this._ensureModal();
		return tmpAPI.loops(this._id).then((pData) =>
		{
			this._loops = (pData && Array.isArray(pData.Loops)) ? pData.Loops : [];
			this._shape();
			this.render();
		});
	}

	// Re-fold this._loops (+ the open-loop state) into AppData for the ring cycle. Percent is what the
	// server says to DRAW, which is not always what the checklist measures: a Realized or Abandoned
	// vision has its rings decided by its status (WI #409). This adds the ring geometry, colour, and
	// open class on top of that.
	_shape()
	{
		let tmpLoops = this._loops.map((pLoop) =>
		{
			let tmpPct = Math.max(0, Math.min(100, Number(pLoop.Percent) || 0));
			let tmpDerived = (pLoop.Source === 'status');
			return Object.assign({}, pLoop,
			{
				ViewHash: this.Hash,
				Color: (tmpDerived && pLoop.Stage === 'stopped') ? _STOPPED : (_COLORS[pLoop.Key] || '#888888'),
				Dash: ((tmpPct / 100) * _CIRC).toFixed(1) + ' ' + _CIRC.toFixed(1),
				// A DERIVED RING NEVER PRINTS A PERCENTAGE, because it did not count one. Writing 100%
				// in the middle of a ring nobody ticked would swap one false number for another; the
				// word says the same thing without claiming a measurement.
				PercentLabel: libLoopStatus.ringLabel(pLoop),
				PercentClass: tmpDerived ? 'ps-loop-ring-word' : '',
				OpenClass: (pLoop.Key === this._openLoop) ? 'ps-loop-open' : ''
			});
		});
		this.pict.AppData.VisionLoops = { Loops: tmpLoops, Empty: tmpLoops.length ? [] : [{ Message: 'No loop data yet.' }] };
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._renderCycle();
		this._renderDetail();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	// Paint just the ring cycle into its container, leaving the open-checklist panel untouched.
	_renderCycle()
	{
		let tmpHTML = this.pict.parseTemplateByHash('VisionLoops-Cycle', {});
		this.pict.ContentAssignment.assignContent('#VisionLoops-Cycle', tmpHTML);
	}

	/**
	 * Click a ring: toggle that loop's checklist open (or closed if it is already open).
	 *
	 * The open loop rides in the vision's own URL as ?loop=deliver rather than in a route of its own,
	 * because it is a refinement of the page you are already on, not a different page -- the same
	 * reasoning that puts a list's filters in its query. replaceState, so the router is not re-entered
	 * and the checklist below is not torn down and rebuilt under the person using it.
	 */
	openLoop(pKey)
	{
		this._openLoop = (this._openLoop === pKey) ? null : pKey;
		libFocus.writeHashQuery(this._openLoop ? { loop: this._openLoop } : {});
		this._shape();
		this._renderCycle();
		this._renderDetail();
	}

	// Show the open loop's checklist below the rings (or clear the panel). The checklist control is the
	// same pict-section-checklist used elsewhere, bound to the loop's IDChecklist; its onChange refreshes
	// the rings live.
	_renderDetail()
	{
		let tmpDetail = '#VisionLoops-Detail';
		let tmpLoop = this._openLoop ? this._loops.find((pLoop) => pLoop.Key === this._openLoop) : null;
		if (!tmpLoop || !tmpLoop.IDChecklist)
		{
			this.pict.ContentAssignment.assignContent(tmpDetail, '');
			return;
		}
		let tmpPanel = this.pict.parseTemplateByHash('VisionLoops-Panel', { ViewHash: this.Hash, Key: tmpLoop.Key, Label: tmpLoop.Label });
		this.pict.ContentAssignment.assignContent(tmpDetail, tmpPanel);
		this._mountLoopChecklist(tmpLoop);
	}

	_mountLoopChecklist(pLoop)
	{
		let tmpID = pLoop.IDChecklist;
		let tmpHash = 'PSLoopChecklist-' + tmpID;
		let tmpMount = '#VisionLoops-Checklist-Mount';
		let tmpView = this.pict.views[tmpHash];
		if (!tmpView)
		{
			tmpView = this.pict.addView(tmpHash, Object.assign({}, libChecklist.default_configuration,
				{
					ViewIdentifier: tmpHash,
					AutoRender: false,
					Context: { OwnerType: 'Checklist', IDOwner: String(tmpID) },
					DataProvider: this._provider,
					Title: pLoop.Label,
					EditableTitle: false,
					onChange: () => { this._onLoopChange(); },
					DefaultDestinationAddress: tmpMount,
					Renderables: [{ RenderableHash: 'Checklist-Section', TemplateHash: 'Checklist-Section', ContentDestinationAddress: tmpMount, RenderMethod: 'replace' }]
				}), libChecklist);
		}
		if (!this._childUp[tmpHash])
		{
			// pict's addView after app init does NOT run the view lifecycle, so bring the control up by
			// hand: the sync stages via initialize(), then mount() (wires the provider, loads, paints).
			this._childUp[tmpHash] = true;
			if (typeof tmpView.initialize === 'function' && !tmpView.initializeTimestamp) { tmpView.initialize(); }
			if (typeof tmpView.mount === 'function') { try { tmpView.mount(); } catch (pError) { /* ignore */ } }
			else { try { tmpView.render(); } catch (pError) { /* ignore */ } }
		}
		else
		{
			// Re-opened: repaint the existing control into the fresh mount node.
			try { tmpView.render(); } catch (pError) { /* ignore */ }
		}
	}

	// A checklist item changed: re-fetch the loops and repaint the rings (the open checklist stays put).
	_onLoopChange()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || !this._id) { return; }
		tmpAPI.loops(this._id).then((pData) =>
		{
			this._loops = (pData && Array.isArray(pData.Loops)) ? pData.Loops : [];
			this._shape();
			this._renderCycle();
		}).catch(() => { /* leave the rings as they are */ });
	}

	_ensureModal()
	{
		if (!this.pict.views['Pict-Section-Modal']) { this.pict.addView('Pict-Section-Modal', libModal.default_configuration, libModal); }
		let tmpModalView = this.pict.views['Pict-Section-Modal'];
		if (tmpModalView && typeof document !== 'undefined' && document.body && typeof tmpModalView.initialize === 'function') { tmpModalView.initialize(); }
	}
}

module.exports = PictViewVisionLoops;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-VisionLoops',
	DefaultRenderable: 'VisionLoops-Display',
	DefaultDestinationAddress: '#Entity-VisionLoops',
	AutoRender: false,
	CSS: /*css*/`
		.ps-loops { display: flex; flex-direction: column; gap: 8px; }
		.ps-loops-cycle { display: flex; align-items: stretch; gap: 0; flex-wrap: wrap; }
		.ps-loop { flex: 1 1 150px; display: flex; flex-direction: column; align-items: center; text-align: center; padding: 8px 10px; position: relative; cursor: pointer; border-radius: 10px; border: 1px solid transparent; transition: background 0.12s ease, border-color 0.12s ease; }
		.ps-loop:hover { background: var(--theme-color-background-hover, #eef2f6); }
		.ps-loop.ps-loop-open { background: var(--theme-color-background-secondary, #eef1f6); border-color: var(--theme-color-border-default, #d8dde6); }
		.ps-loop:not(:last-child)::after { content: '→'; position: absolute; right: -7px; top: 40px; color: var(--theme-color-text-muted, #b3bcc6); font-size: 18px; }
		.ps-loop-ring { width: 84px; height: 84px; }
		.ps-loop-ring svg { width: 84px; height: 84px; display: block; }
		.ps-loop-ring-track { stroke: var(--theme-color-background-tertiary, #e3e8f0); }
		.ps-loop-ring-pct { fill: var(--theme-color-text-primary, #2c3140); font-size: 19px; font-weight: 700; }
		/* A word needs less room than a percentage and should not shout as loudly as a measured one. */
		.ps-loop-ring-word { font-size: 14px; font-weight: 600; fill: var(--theme-color-text-secondary, #5b6376); }
		.ps-loop-label { font-size: 13px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); margin-top: 6px; }
		.ps-loop-stage { font-size: 12px; color: var(--theme-color-text-secondary, #5b6376); margin-top: 1px; }
		.ps-loop-detail { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); margin-top: 2px; }
		.ps-loops-note { font-size: 11px; color: var(--theme-color-text-muted, #97a1ab); text-align: center; font-style: italic; }
		.ps-loop-panel { margin-top: 6px; }
		.ps-loop-panel-inner { border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 14px 16px; background: var(--theme-color-background-panel, #fff); }
		.ps-loop-panel-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
		.ps-loop-panel-head h4 { margin: 0; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-loop-panel-close { font: inherit; font-size: 12px; border: none; background: none; color: var(--theme-color-brand-primary, #2880a6); cursor: pointer; padding: 2px 6px; }
		.ps-loop-panel-close:hover { text-decoration: underline; }
	`,
	Templates:
	[
		{
			Hash: 'VisionLoops-Display',
			Template: /*html*/`
<div class="ps-loops">
	<div class="ps-loops-cycle" id="VisionLoops-Cycle"></div>
	<div class="ps-loops-note">Design feeds the build; the build is delivered to the customer; their feedback feeds the next design. Click a loop to work its checklist.</div>
	<div class="ps-loop-panel" id="VisionLoops-Detail"></div>
</div>`
		},
		{
			Hash: 'VisionLoops-Cycle',
			Template: /*html*/`{~TS:Loop-Card:AppData.VisionLoops.Loops~}`
		},
		{
			Hash: 'Loop-Card',
			Template: /*html*/`
<div class="ps-loop {~D:Record.OpenClass~}" onclick="_Pict.views['{~D:Record.ViewHash~}'].openLoop('{~D:Record.Key~}')">
	<div class="ps-loop-ring">
		<svg viewBox="0 0 80 80">
			<circle class="ps-loop-ring-track" cx="40" cy="40" r="34" fill="none" stroke-width="8"></circle>
			<circle cx="40" cy="40" r="34" fill="none" stroke="{~D:Record.Color~}" stroke-width="8" stroke-linecap="round" stroke-dasharray="{~D:Record.Dash~}" transform="rotate(-90 40 40)"></circle>
			<text class="ps-loop-ring-pct {~D:Record.PercentClass~}" x="40" y="47" text-anchor="middle">{~D:Record.PercentLabel~}</text>
		</svg>
	</div>
	<div class="ps-loop-label">{~D:Record.Label~}</div>
	<div class="ps-loop-stage">{~D:Record.Stage~}</div>
	<div class="ps-loop-detail">{~D:Record.Detail~}</div>
</div>`
		},
		{
			Hash: 'VisionLoops-Panel',
			Template: /*html*/`
<div class="ps-loop-panel-inner">
	<div class="ps-loop-panel-head"><h4>{~D:Record.Label~}</h4><button class="ps-loop-panel-close" onclick="_Pict.views['{~D:Record.ViewHash~}'].openLoop('{~D:Record.Key~}')">Close</button></div>
	<div class="ps-loop-checklist-mount" id="VisionLoops-Checklist-Mount"></div>
</div>`
		}
	],
	Renderables:
	[
		{
			RenderableHash: 'VisionLoops-Display',
			TemplateHash: 'VisionLoops-Display',
			ContentDestinationAddress: '#Entity-VisionLoops',
			RenderMethod: 'replace'
		}
	]
};
